import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export function CheckEmailPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email || '';
    const [isResending, setIsResending] = useState(false);

    const handleResend = async () => {
        if (!email) {
            toast.error('Email address not found');
            return;
        }

        setIsResending(true);
        try {
            await api.request('/auth/resend-verification', {
                method: 'POST',
                body: JSON.stringify({ email }),
            });
            toast.success('Verification email sent! Please check your inbox.');
        } catch (err) {
            toast.error(err.message || 'Failed to resend email');
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <div className="max-w-md w-full text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Mail className="w-10 h-10 text-primary" />
                </div>

                <h1 className="text-3xl font-bold mb-3">Check your email</h1>
                
                <p className="text-muted-foreground mb-2">
                    We've sent a verification link to
                </p>
                
                <p className="font-semibold text-lg mb-6">
                    {email || 'your email address'}
                </p>

                <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6 text-sm text-left">
                    <p className="font-medium mb-2">Next steps:</p>
                    <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Check your inbox (and spam folder)</li>
                        <li>Click the verification link in the email</li>
                        <li>You'll be automatically logged in</li>
                    </ol>
                </div>

                {email && !email.includes('jack@misfitsmanaged.org') && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-sm text-left">
                        <p className="font-medium text-yellow-800 mb-1">⚠️ Testing Mode Active</p>
                        <p className="text-yellow-700">
                            Email delivery is currently in testing mode. Verification emails can only be sent to <strong>jack@misfitsmanaged.org</strong>. 
                            To receive emails at other addresses, please verify a domain in the Resend dashboard.
                        </p>
                    </div>
                )}

                <div className="space-y-3">
                    <Button 
                        onClick={handleResend} 
                        variant="outline" 
                        className="w-full"
                        disabled={isResending}
                    >
                        {isResending ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Sending...
                            </>
                        ) : (
                            'Resend verification email'
                        )}
                    </Button>

                    <Button 
                        onClick={() => navigate('/auth')} 
                        variant="ghost" 
                        className="w-full"
                    >
                        Back to login
                    </Button>
                </div>

                <p className="text-xs text-muted-foreground mt-8">
                    The verification link will expire in 24 hours
                </p>
            </div>
        </div>
    );
}
